import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  FileCheck, Megaphone, Users, CheckCircle2, Clock, ArrowRight, TrendingUp,
  Plus, Hammer, CalendarCheck,
} from 'lucide-react';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { storage, formatDate, relativeTime } from '@/lib/storage';
import {
  CHART_REQUEST_TREND, CHART_CONCERN_CATEGORY, ACCOMPLISHMENT_METRICS,
  SAMPLE_REQUESTS, SAMPLE_CONCERNS,
} from '@/data/mockData';
import { auth } from '@/lib/auth';

const PIE_COLORS = ['hsl(224, 76%, 48%)', 'hsl(186, 88%, 42%)', 'hsl(38, 92%, 50%)', 'hsl(148, 60%, 42%)', 'hsl(262, 60%, 55%)', 'hsl(0, 74%, 52%)'];

const AdminDashboard = () => {
  const user = auth.user();
  const [requests, setRequests] = useState([]);
  const [concerns, setConcerns] = useState([]);

  useEffect(() => {
    let r = storage.get('requests', null);
    if (r === null) {
      r = SAMPLE_REQUESTS.map((x) => ({
        ...x,
        submittedAt: new Date(x.date).toISOString(),
        timeline: [{ status: x.status, date: new Date(x.date).toISOString(), note: 'Sample seed entry.' }],
      }));
      storage.set('requests', r);
    }
    let c = storage.get('concerns', null);
    if (c === null) {
      c = SAMPLE_CONCERNS.map((x) => ({
        ...x,
        submittedAt: new Date(x.date).toISOString(),
        purok: x.location.split(',')[0]?.trim() || '',
        description: 'Sample concern submission for prototype demo.',
        anonymous: x.name === 'Anonymous',
        timeline: [{ status: x.status, date: new Date(x.date).toISOString(), note: 'Sample seed entry.' }],
      }));
      storage.set('concerns', c);
    }
    setRequests(r);
    setConcerns(c);
  }, []);

  const stats = useMemo(() => {
    const submittedReq = requests.filter((r) => r.status === 'Submitted' || r.status === 'For Review').length;
    const readyReq = requests.filter((r) => r.status === 'Ready for Pickup').length;
    const activeConcerns = concerns.filter((c) => !['Resolved', 'Closed', 'Invalid / Duplicate'].includes(c.status)).length;
    const resolvedConcerns = concerns.filter((c) => c.status === 'Resolved').length;
    return { submittedReq, readyReq, activeConcerns, resolvedConcerns };
  }, [requests, concerns]);

  const recentRequests = [...requests].slice(0, 5);
  const recentConcerns = [...concerns].slice(0, 5);

  return (
    <div data-testid="admin-dashboard-page">
      <AdminPageHeader
        title={`Welcome back, ${user?.displayName?.split('·')[0]?.trim() || 'Admin'} 👋`}
        description="A snapshot of barangay operations — requests, concerns, and content."
        actions={
          <>
            <Button asChild variant="outline" data-testid="dashboard-export">
              <Link to="/admin/export">Export reports</Link>
            </Button>
            <Button asChild className="bg-gradient-primary text-primary-foreground" data-testid="dashboard-create">
              <Link to="/admin/content/announcements"><Plus className="mr-2 h-4 w-4" /> New announcement</Link>
            </Button>
          </>
        }
      />

      {/* KPI cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={FileCheck}
          label="Pending Requests"
          big={requests.filter((r) => ['Submitted', 'For Review', 'Incomplete Requirements', 'Approved'].includes(r.status)).length}
          sub="Currently in queue"
          accent="primary"
        />
        <KpiCard icon={CheckCircle2} label="Ready for Pickup" big={stats.readyReq} sub="Documents waiting at hall" accent="success" />
        <KpiCard icon={Megaphone} label="Active Concerns" big={stats.activeConcerns} sub="Awaiting action / in progress" accent="warning" />
        <KpiCard icon={Users} label="Total Submissions" big={requests.length + concerns.length} sub="Requests + concerns combined" accent="accent" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-5 mt-8">
        <Card className="lg:col-span-2 p-6 border-0 shadow-md-soft">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-heading font-semibold inline-flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" /> Document Requests Trend
              </h3>
              <p className="text-xs text-muted-foreground">Past 6 months · by service type</p>
            </div>
            <Badge variant="secondary" className="bg-primary/10 text-primary">+18% MoM</Badge>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={CHART_REQUEST_TREND} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="hsl(224 76% 48%)" stopOpacity={0.6} /><stop offset="1" stopColor="hsl(224 76% 48%)" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="hsl(186 88% 42%)" stopOpacity={0.5} /><stop offset="1" stopColor="hsl(186 88% 42%)" stopOpacity={0} /></linearGradient>
                <linearGradient id="g3" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="hsl(38 92% 50%)" stopOpacity={0.5} /><stop offset="1" stopColor="hsl(38 92% 50%)" stopOpacity={0} /></linearGradient>
                <linearGradient id="g4" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="hsl(148 60% 42%)" stopOpacity={0.5} /><stop offset="1" stopColor="hsl(148 60% 42%)" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="clearance" stroke="hsl(224 76% 48%)" fill="url(#g1)" name="Clearance" />
              <Area type="monotone" dataKey="indigency" stroke="hsl(186 88% 42%)" fill="url(#g2)" name="Indigency" />
              <Area type="monotone" dataKey="business" stroke="hsl(38 92% 50%)" fill="url(#g3)" name="Business" />
              <Area type="monotone" dataKey="residency" stroke="hsl(148 60% 42%)" fill="url(#g4)" name="Residency" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 border-0 shadow-md-soft">
          <h3 className="font-heading font-semibold inline-flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-accent" /> Concerns by Category
          </h3>
          <p className="text-xs text-muted-foreground">Breakdown of community-reported issues</p>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={CHART_CONCERN_CATEGORY} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                {CHART_CONCERN_CATEGORY.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            {CHART_CONCERN_CATEGORY.map((c, i) => (
              <div key={c.name} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                <span className="text-muted-foreground">{c.name}</span>
                <span className="font-semibold ml-auto">{c.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Recent activity */}
      <div className="grid lg:grid-cols-2 gap-5 mt-8">
        <RecentList
          title="Recent Service Requests"
          link="/admin/requests"
          items={recentRequests.length > 0 ? recentRequests : []}
          renderItem={(r) => (
            <>
              <div className="font-medium text-sm truncate">{r.type}</div>
              <div className="text-xs text-muted-foreground truncate">{r.name} · {r.id}</div>
            </>
          )}
          emptyText="No requests yet."
        />
        <RecentList
          title="Recent Concern Reports"
          link="/admin/concerns"
          items={recentConcerns.length > 0 ? recentConcerns : []}
          renderItem={(c) => (
            <>
              <div className="font-medium text-sm truncate">{c.category}</div>
              <div className="text-xs text-muted-foreground truncate">{c.location} · {c.id}</div>
            </>
          )}
          emptyText="No concerns yet."
        />
      </div>

      {/* Accomplishments quick view */}
      <Card className="mt-8 p-6 border-0 shadow-md-soft">
        <h3 className="font-heading font-semibold inline-flex items-center gap-2">
          <Hammer className="h-4 w-4 text-primary" /> At a Glance · Year to Date
        </h3>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {ACCOMPLISHMENT_METRICS.map((m) => (
            <div key={m.label} className="rounded-xl border bg-secondary/40 p-4 text-center">
              <CalendarCheck className="h-4 w-4 mx-auto text-primary" />
              <div className="mt-2 font-display text-xl font-bold">{m.value.toLocaleString()}+</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.label}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, big, sub, accent }) => {
  const tones = {
    primary: 'from-primary to-primary-glow',
    success: 'from-success to-emerald-400',
    warning: 'from-warning to-amber-400',
    accent: 'from-accent to-cyan-400',
  };
  return (
    <Card className="relative p-5 border-0 shadow-md-soft overflow-hidden" data-testid={`kpi-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${tones[accent]} opacity-10`} />
      <Icon className="h-5 w-5 text-primary" />
      <div className="mt-3 font-display text-3xl font-bold">{big}</div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
    </Card>
  );
};

const RecentList = ({ title, link, items, renderItem, emptyText }) => (
  <Card className="p-6 border-0 shadow-md-soft">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-heading font-semibold">{title}</h3>
      <Button asChild variant="ghost" size="sm">
        <Link to={link}>View all <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
      </Button>
    </div>
    {items.length === 0 ? (
      <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-xl">{emptyText}</div>
    ) : (
      <ul className="divide-y">
        {items.map((it) => (
          <li key={it.id} className="py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">{renderItem(it)}</div>
            <div className="flex items-center gap-3">
              <StatusBadge status={it.status} />
              <span className="text-[11px] text-muted-foreground hidden sm:inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {relativeTime(it.submittedAt || it.date)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

export default AdminDashboard;
